import type { CourseDocument } from './CourseDocument';
import { compileCourseDocument } from './CourseCompiler';
import { BUILT_IN_COURSES, loadBuiltInCourse } from './data/builtInCourses';
import { parseCourseDocumentJson, validateCourseDocument } from './CourseDocumentValidator';

export const COURSE_EDITOR_STORAGE_KEY = 'golf-sim.course-editor.document';
export const IMPORTED_COURSES_STORAGE_KEY = 'golf-sim.imported-courses';

export type CourseSourceType = 'built-in' | 'my-course' | 'imported';
export type CourseValidationStatus = 'valid' | 'invalid';

export interface CourseLibraryEntry {
  id: string;
  name: string;
  description: string;
  thumbnail: { kind: 'gradient'; label: string };
  holeCount: number;
  totalPar: number;
  totalYardage: number;
  source: CourseSourceType;
  lastModified?: number;
  validationStatus: CourseValidationStatus;
  validationErrors: string[];
  document?: CourseDocument;
}

export class CourseLibrary {
  constructor(private readonly storage: Storage = window.localStorage) {}

  list(): CourseLibraryEntry[] {
    // Registrations are lightweight: documents are generated only for the selected course.
    return [...BUILT_IN_COURSES.map((course) => this.builtInEntry(course)), ...this.savedEntries()];
  }

  find(id: string): CourseLibraryEntry | undefined {
    return this.list().find((entry) => entry.id === id);
  }

  compile(id: string) {
    const entry = this.find(id);
    const document = entry?.source === 'built-in' ? loadBuiltInCourse(id) : entry?.document;
    if (!document || entry?.validationStatus !== 'valid') return null;
    return compileCourseDocument(document);
  }

  private builtInEntry(registration: (typeof BUILT_IN_COURSES)[number]): CourseLibraryEntry {
    return {
      id: registration.id,
      name: registration.name,
      description: registration.description,
      thumbnail: { kind: 'gradient', label: registration.name },
      holeCount: registration.holeCount,
      totalPar: registration.totalPar,
      totalYardage: registration.totalYardage,
      source: 'built-in',
      validationStatus: 'valid',
      validationErrors: [],
    };
  }

  private savedEntries(): CourseLibraryEntry[] {
    const entries: CourseLibraryEntry[] = [];
    const saved = this.storage.getItem(COURSE_EDITOR_STORAGE_KEY);
    if (saved) entries.push(this.parseEntry(saved, 'my-course', Date.now(), 'last-saved-course'));
    const imported = this.storage.getItem(IMPORTED_COURSES_STORAGE_KEY);
    if (imported) {
      const result = parseCourseDocumentJson(imported);
      if (result.document) entries.push(this.entryFor(result.document, 'imported', Date.now()));
    }
    return entries;
  }

  private parseEntry(
    json: string,
    source: CourseSourceType,
    lastModified?: number,
    fallbackId = 'saved-course',
  ): CourseLibraryEntry {
    const parsed = parseCourseDocumentJson(json);
    if (parsed.document) return this.entryFor(parsed.document, source, lastModified);
    return {
      id: fallbackId,
      name: 'Saved course unavailable',
      description: 'This saved course could not be loaded safely.',
      thumbnail: { kind: 'gradient', label: 'Invalid' },
      holeCount: 0,
      totalPar: 0,
      totalYardage: 0,
      source,
      lastModified,
      validationStatus: 'invalid',
      validationErrors: parsed.errors,
    };
  }

  private entryFor(
    document: CourseDocument,
    source: CourseSourceType,
    lastModified?: number,
  ): CourseLibraryEntry {
    const validation = validateCourseDocument(document);
    return {
      id: document.id,
      name: document.name,
      description: document.description,
      thumbnail: { kind: 'gradient', label: document.name },
      holeCount: document.holes.length,
      totalPar: document.holes.reduce((sum, hole) => sum + hole.par, 0),
      totalYardage: document.holes.reduce((sum, hole) => sum + hole.yardage, 0),
      source,
      lastModified,
      validationStatus: validation.valid ? 'valid' : 'invalid',
      validationErrors: validation.errors,
      document: validation.valid ? document : undefined,
    };
  }
}
